import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MuscleStatistics } from 'src/app/home/statistics/statistics.component';

import { WorkoutPlan, PlanHistory, ExercisePlan } from 'src/models/plan.model';
import { ExerciseService } from 'src/services/exercise-service.service';
import { MuscleGroupService } from 'src/services/muscle-group.service';
import { PlanService } from 'src/services/plan.service';

@Component({
    selector: 'app-plan-summary',
    templateUrl: './plan-summary.component.html',
    styleUrls: ['./plan-summary.component.scss'],
    standalone: false
})
export class PlanSummaryComponent implements OnInit {
  @Input() workoutPlanId: string | null = null;
  workoutPlan: WorkoutPlan | null = null;
  primaryMuscleGroups: Set<string> = new Set();
  secondaryMuscleGroups: Set<string> = new Set();
  averageWorkoutTime: number | null = null;
  formattedWorkoutTime: string | null = null;
  muscleStatistics: MuscleStatistics[] = [];
  exercises: any[] = [];

  @Output() name = new EventEmitter<string>();

  constructor(
    private route: ActivatedRoute,
    private planService: PlanService,
    private exerciseService: ExerciseService,
    private muscleGroupsService: MuscleGroupService
  ) {}

  async ngOnInit() {
    if (this.workoutPlanId) {
      await this.loadWorkoutPlan();
      await this.calculateAverageWorkoutTime();
      await this.loadExerciseDetails();
    }
  }

  private async loadWorkoutPlan() {
    try {
      const plan = await this.planService.getWorkoutPlanById(
        this.workoutPlanId!
      );
      if (plan) {
        this.workoutPlan = plan;
        this.name.emit(plan.planName);
        await this.extractMuscleGroups(plan);
        this.removePrimaryFromSecondary();
        const statistics = this.getMuscleStatistics(
          this.primaryMuscleGroups,
          this.secondaryMuscleGroups
        );
        this.muscleStatistics =
          this.muscleGroupsService.mapMuscleGroups(statistics);
      }
    } catch (error) {
      console.error('Error loading workout plan:', error);
    }
  }

  private async loadExerciseDetails() {
    if (!this.workoutPlan) return;

    const exerciseIds = this.workoutPlan.exercises.map((ex) => ex.id);
    this.exercises = await Promise.all(
      exerciseIds.map(async (id) => {
        const exercise = await this.exerciseService.getExercise(id);
        if (exercise) {
          // Find the corresponding ExercisePlan data for sets/reps
          const exercisePlan = this.workoutPlan!.exercises.find(
            (ex) => ex.id === id
          );
          return {
            ...exercise,
            sets: exercisePlan?.sets || 0,
            reps: exercisePlan?.reps || 0, // Adjust this line according to your data structure
          };
        }
        return null;
      })
    );

    // Filter out any null values in case some exercises were not found
    this.exercises = this.exercises.filter((exercise) => exercise !== null);
  }

  private async extractMuscleGroups(plan: WorkoutPlan) {
    const exerciseIds = plan.exercises.map((ex) => ex.id);
    const exercises = await Promise.all(
      exerciseIds.map((id) => this.exerciseService.getExercise(id))
    );
    exercises.forEach((exercise) => {
      if (exercise) {
        if (exercise.hashtags)
          exercise.hashtags.forEach((tag) => this.primaryMuscleGroups.add(tag));
        if (exercise.secondaryHashtags)
          exercise.secondaryHashtags.forEach((tag) =>
            this.secondaryMuscleGroups.add(tag)
          );
      }
    });
  }

  private async calculateAverageWorkoutTime() {
    try {
      const histories = await this.planService.getPlanHistoriesByPlanId(
        this.workoutPlanId!
      );
      if (histories.length > 0) {
        const durations = histories.map((history) => {
          const startTime = history.startTime.toDate();
          const finishTime = history.finishTime.toDate();
          return finishTime.getTime() - startTime.getTime();
        });

        durations.sort((a, b) => a - b);
        const q1 = this.getPercentile(durations, 0.25);
        const q3 = this.getPercentile(durations, 0.75);
        const iqr = q3 - q1;

        const lowerBound = q1 - 1.5 * iqr;
        const upperBound = q3 + 1.5 * iqr;

        const validDurations = durations.filter(
          (duration) => duration >= lowerBound && duration <= upperBound
        );

        if (validDurations.length > 0) {
          const totalTime = validDurations.reduce(
            (sum, duration) => sum + duration,
            0
          );
          this.averageWorkoutTime =
            totalTime / validDurations.length / (1000 * 60);
          this.formattedWorkoutTime = this.formatTime(this.averageWorkoutTime);
        } else {
          this.averageWorkoutTime = null;
        }
      }
    } catch (error) {
      console.error('Error calculating average workout time:', error);
    }
  }

  private getPercentile(arr: number[], percentile: number) {
    const index = Math.floor(percentile * arr.length);
    return arr[index];
  }

  removePrimaryFromSecondary() {
    this.primaryMuscleGroups.forEach((primary) => {
      if (this.secondaryMuscleGroups.has(primary)) {
        this.secondaryMuscleGroups.delete(primary);
      }
    });
  }

  private getMuscleStatistics(
    primaryMuscleGroups: Set<string>,
    secondaryMuscleGroups: Set<string>
  ): MuscleStatistics[] {
    const muscleStatistics: MuscleStatistics[] = [];

    primaryMuscleGroups.forEach((muscle) => {
      muscleStatistics.push({ muscle, value: 1 });
    });

    secondaryMuscleGroups.forEach((muscle) => {
      muscleStatistics.push({ muscle, value: 0.5 });
    });

    return muscleStatistics;
  }

  private formatTime(totalMinutes: number | null): string | null {
    if (totalMinutes === null) {
      return null;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  }
}
